import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { demoConversations, demoUser, demoSteps } from '../demo/data/demoData';

const DemoContext = createContext();

export const useDemoContext = () => {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error('useDemoContext must be used within a DemoProvider');
  }
  return context;
};

export const DemoProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Initialize demo mode state from sessionStorage
  const [isDemoMode, setIsDemoMode] = useState(() => {
    const stored = sessionStorage.getItem('fwd-demo-mode');
    return stored === 'true';
  });
  
  const [demoData, setDemoData] = useState(() => {
    const storedData = sessionStorage.getItem('fwd-demo-data');
    if (storedData) {
      try {
        return JSON.parse(storedData);
      } catch (error) {
        console.error('Error parsing stored demo data:', error);
      }
    }
    return {
      currentStep: 1,
      totalSteps: demoSteps.length,
      conversations: [],
      currentConversation: null,
      demoProgress: {},
      user: null,
      steps: demoSteps
    };
  });

  const startDemo = useCallback(() => {
    setIsDemoMode(true);
    sessionStorage.setItem('fwd-demo-mode', 'true');
    
    // Clear any old demo-related localStorage that might cause navigation issues
    localStorage.removeItem('demoQuestionScores');
    localStorage.removeItem('demo-questionnaire-state');
    localStorage.removeItem('demo-results-url');
    
    const newDemoData = {
      currentStep: 1,
      totalSteps: demoSteps.length,
      conversations: demoConversations,
      currentConversation: null,
      demoProgress: {},
      user: demoUser,
      steps: demoSteps
    };
    
    setDemoData(newDemoData);
    sessionStorage.setItem('fwd-demo-data', JSON.stringify(newDemoData));
  }, []);

  const endDemo = useCallback(() => {
    setIsDemoMode(false);
    sessionStorage.removeItem('fwd-demo-mode');
    sessionStorage.removeItem('fwd-demo-data');
    
    // Clear any demo-related localStorage
    localStorage.removeItem('demoQuestionScores');
    localStorage.removeItem('demo-questionnaire-state');
    localStorage.removeItem('demo-results-url');
    
    setDemoData({
      currentStep: 1,
      totalSteps: demoSteps.length,
      conversations: [],
      currentConversation: null,
      demoProgress: {},
      user: null,
      steps: demoSteps
    });
    // Navigate back to the production conversations page
    navigate('/conversations');
  }, [navigate]);

  const updateDemoStep = useCallback((step) => {
    setDemoData(prev => {
      const updatedData = {
        ...prev,
        currentStep: step
      };
      if (isDemoMode) {
        sessionStorage.setItem('fwd-demo-data', JSON.stringify(updatedData));
      }
      return updatedData;
    });
  }, [isDemoMode]);

  const updateDemoProgress = useCallback((stepKey, progress) => {
    setDemoData(prev => {
      const updatedData = {
        ...prev,
        demoProgress: {
          ...prev.demoProgress,
          [stepKey]: progress
        }
      };
      if (isDemoMode) {
        sessionStorage.setItem('fwd-demo-data', JSON.stringify(updatedData));
      }
      return updatedData;
    });
  }, [isDemoMode]);

  const updateDemoData = useCallback((newData) => {
    setDemoData(prev => {
      const updatedData = {
        ...prev,
        ...newData
      };
      // Persist to sessionStorage
      if (isDemoMode) {
        sessionStorage.setItem('fwd-demo-data', JSON.stringify(updatedData));
      }
      return updatedData;
    });
  }, [isDemoMode]);

  const addCompletedConversation = useCallback((conversation) => {
    setDemoData(prev => {
      const updatedData = {
        ...prev,
        completedConversations: [
          ...(prev.completedConversations || []),
          conversation
        ]
      };
      // Persist to sessionStorage
      if (isDemoMode) {
        sessionStorage.setItem('fwd-demo-data', JSON.stringify(updatedData));
      }
      return updatedData;
    });
  }, [isDemoMode]);

  const nextDemoStep = useCallback(() => {
    setDemoData(prev => {
      const updatedData = {
        ...prev,
        currentStep: Math.min(prev.currentStep + 1, prev.totalSteps)
      };
      if (isDemoMode) {
        sessionStorage.setItem('fwd-demo-data', JSON.stringify(updatedData));
      }
      return updatedData;
    });
  }, [isDemoMode]);

  const prevDemoStep = useCallback(() => {
    setDemoData(prev => {
      const updatedData = {
        ...prev,
        currentStep: Math.max(prev.currentStep - 1, 1)
      };
      if (isDemoMode) {
        sessionStorage.setItem('fwd-demo-data', JSON.stringify(updatedData));
      }
      return updatedData;
    });
  }, [isDemoMode]);

  // Function to update demo phase based on completion
  const updateDemoPhase = useCallback((phase) => {
    const phaseToStepMap = {
      'started': 1,
      'questionnaire-started': 4,
      'questionnaire-complete': 5,
      'self-guided-topics': 5,
      'results-topics': 6,
      'topics-selected': 7,
      'prep-complete': 9,
      'demo-complete': 10
    };
    
    const targetStep = phaseToStepMap[phase] || 1;
    
    setDemoData(prev => {
      const updatedData = {
        ...prev,
        currentStep: targetStep,
        currentPhase: phase
      };
      if (isDemoMode) {
        sessionStorage.setItem('fwd-demo-data', JSON.stringify(updatedData));
      }
      return updatedData;
    });
  }, [isDemoMode]);

  // Effect to handle URL-based demo mode detection and cleanup
  React.useEffect(() => {
    const currentPath = location.pathname;
    const isDemoURL = currentPath.startsWith('/demo');
    
    // If we're on a demo URL but not in demo mode, start demo mode
    if (isDemoURL && !isDemoMode) {
      startDemo();
    }
    
    // If we're not on a demo URL and we're in demo mode, end demo mode
    if (!isDemoURL && isDemoMode) {
      endDemo();
    }
  }, [location.pathname, isDemoMode, startDemo, endDemo]);

  const value = useMemo(() => ({
    isDemoMode,
    demoData,
    startDemo,
    endDemo,
    updateDemoStep,
    updateDemoProgress,
    updateDemoData,
    addCompletedConversation,
    nextDemoStep,
    prevDemoStep,
    updateDemoPhase
  }), [isDemoMode, demoData, startDemo, endDemo, updateDemoStep, updateDemoProgress, updateDemoData, addCompletedConversation, nextDemoStep, prevDemoStep, updateDemoPhase]);

  return (
    <DemoContext.Provider value={value}>
      {children}
    </DemoContext.Provider>
  );
};

DemoProvider.propTypes = {
  children: PropTypes.node.isRequired
};
